import { useMemo } from 'react';
import { useRunbookLog } from 'infrastructure/hooks/clinic/use-runbook-logs';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

interface IProps {
  patientGuid: string;
}

export interface IClinicalRunbook {
  loading: boolean;
  /** true when the clinic has a critical-escalation instruction for the current time of day */
  hasCriticalEntry: boolean;
  /** the critical instruction that applies right now (business vs after hours), stripped of HTML */
  criticalInstruction: string;
  criticalBusinessHours: string;
  criticalAfterHours: string;
  contactMethod: string;
  notes: string;
}

/** Strip Quill/HTML tags and collapse whitespace so rich-text instructions render as plain text. */
const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Business hours = 08:00–17:59 local; outside that is after hours. */
const isBusinessHours = (hour: number): boolean => hour >= 8 && hour < 18;

/**
 * Resolve the current clinic's runbook (clinic instructions) for the patient and
 * expose the critical-escalation instruction that applies right now, plus whether
 * such an entry exists. Mirrors how the clinical-escalation form reads the runbook
 * (useRunbookLog by clinicGuid, items[0] = current), but focused on the critical fields.
 */
export const useClinicalRunbook = ({
  patientGuid,
}: IProps): IClinicalRunbook => {
  const { patientInfo } = usePatientInfo({ patientGuid });
  const clinicGuid = patientInfo?.clinicGuid;
  const { runbookLogs, loading } = useRunbookLog({
    meta: { clinicGuid, items: 1 },
  });

  const current = runbookLogs?.items?.[0];

  // Memoize the returned object so its identity is stable across renders (only changes
  // when the underlying runbook or loading state changes). A new object each render
  // would bust downstream memos (buildItemsForStep), remounting form fields and
  // dropping input focus after one keystroke.
  return useMemo<IClinicalRunbook>(() => {
    const criticalBusinessHours = stripHtml(current?.criticalBusinessHours);
    const criticalAfterHours = stripHtml(current?.criticalAfterHours);
    const nowHour = new Date().getHours();
    const criticalInstruction = isBusinessHours(nowHour)
      ? criticalBusinessHours
      : criticalAfterHours;

    return {
      loading,
      hasCriticalEntry: Boolean(criticalInstruction),
      criticalInstruction,
      criticalBusinessHours,
      criticalAfterHours,
      contactMethod: stripHtml(current?.contactMethod),
      notes: stripHtml(current?.notes),
    };
  }, [current, loading]);
};
